import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { navLinks } from '../data/data.js';
import { FaPhoneAlt, FaTimes } from 'react-icons/fa';
import { GiHamburgerMenu } from 'react-icons/gi';
import { Link, useLocation } from 'react-router-dom';

function Navbar() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const toggleSidebar = () => setIsOpen(prev => !prev);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <>
      <NavbarContainer scrolled={scrolled}>
        <NavbarInner>
          <LogoLink to="/">
            <BusinessLogo
              alt="Champion & Champion Construction"
              src="https://firebasestorage.googleapis.com/v0/b/champion-building.appspot.com/o/champ%20logo%20v3.png?alt=media&token=f49e1b4f-a54e-4fd5-bbfb-c1432be8d836"
            />
          </LogoLink>

          {windowWidth >= 768 ? (
            <>
              <DesktopNav>
                {navLinks.map((navlink) => (
                  <NavItem key={navlink.id}>
                    <NavLinkStyled
                      to={`/${navlink.link}`}
                      $active={location.pathname === `/${navlink.link}` ? 1 : 0}
                    >
                      {navlink.title}
                    </NavLinkStyled>
                  </NavItem>
                ))}
              </DesktopNav>
              <PhoneBlock>
                <FaPhoneAlt style={{ color: '#c89432', marginRight: '10px', flexShrink: 0 }} />
                <div>
                  <PhoneLink href="tel:+18188095041">1 (818) 809-5041</PhoneLink>
                  <SpanishText>¡Hablamos Español!</SpanishText>
                </div>
              </PhoneBlock>
            </>
          ) : (
            <HamburgerBtn onClick={toggleSidebar} aria-label="Open navigation menu">
              <GiHamburgerMenu />
            </HamburgerBtn>
          )}
        </NavbarInner>
      </NavbarContainer>

      <SidebarOverlay isOpen={isOpen} onClick={toggleSidebar} />
      <Sidebar isOpen={isOpen}>
        <SidebarTop>
          <CloseBtn onClick={toggleSidebar} aria-label="Close menu">
            <FaTimes />
          </CloseBtn>
        </SidebarTop>
        <SidebarLinks>
          {navLinks.map(navlink => (
            <SidebarItem key={navlink.id}>
              <SidebarLink to={`/${navlink.link}`}>{navlink.title}</SidebarLink>
            </SidebarItem>
          ))}
        </SidebarLinks>
        <SidebarPhone href="tel:+18188095041">
          <FaPhoneAlt style={{ marginRight: '10px' }} />
          1 (818) 809-5041
        </SidebarPhone>
        <SidebarFooter>Champion &amp; Champion © 2024</SidebarFooter>
      </Sidebar>
    </>
  );
}

const NavbarContainer = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  background: #fff;
  box-shadow: ${({ scrolled }) =>
    scrolled ? '0 2px 20px rgba(0,0,0,0.12)' : '0 1px 0 #e5e7eb'};
  transition: box-shadow 0.3s ease;
`;

const NavbarInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  @media (max-width: 768px) {
    padding: 0 20px;
    height: 68px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const BusinessLogo = styled.img`
  height: 58px;
  width: auto;
  cursor: pointer;
  @media (max-width: 768px) {
    height: 44px;
  }
`;

const DesktopNav = styled.ul`
  display: flex;
  align-items: center;
  gap: 2px;
  list-style: none;
  flex: 1;
  justify-content: center;
`;

const NavItem = styled.li``;

const NavLinkStyled = styled(Link)`
  display: block;
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  letter-spacing: 0.3px;
  color: ${({ $active }) => ($active ? '#c89432' : '#374151')};
  padding: 8px 14px;
  border-radius: 6px;
  transition: color 0.2s ease, background 0.2s ease;
  border-bottom: ${({ $active }) => ($active ? '2px solid #c89432' : '2px solid transparent')};
  &:hover {
    color: #c89432;
    background: #fef9ed;
  }
`;

const PhoneBlock = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const PhoneLink = styled.a`
  display: block;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  color: #111827;
  line-height: 1.3;
  transition: color 0.2s;
  &:hover {
    color: #c89432;
  }
`;

const SpanishText = styled.span`
  display: block;
  font-family: "Poppins", sans-serif;
  font-size: 0.68rem;
  color: #9ca3af;
  font-style: italic;
  line-height: 1;
`;

const HamburgerBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  color: #111827;
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 6px;
  transition: color 0.2s;
  &:hover {
    color: #c89432;
  }
`;

const SidebarOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 200;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  pointer-events: ${({ isOpen }) => (isOpen ? 'all' : 'none')};
  transition: opacity 0.3s ease;
`;

const Sidebar = styled.div`
  position: fixed;
  top: 0;
  right: ${({ isOpen }) => (isOpen ? '0' : '-320px')};
  width: 300px;
  height: 100%;
  background: #111827;
  z-index: 300;
  transition: right 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  @media (max-width: 400px) {
    width: 100%;
    right: ${({ isOpen }) => (isOpen ? '0' : '-100%')};
  }
`;

const SidebarTop = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 16px 8px;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: color 0.2s;
  &:hover {
    color: #fff;
  }
`;

const SidebarLinks = styled.ul`
  list-style: none;
  padding: 8px 0;
  flex: 1;
`;

const SidebarItem = styled.li`
  border-bottom: 1px solid #1f2937;
`;

const SidebarLink = styled(Link)`
  display: block;
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  font-size: 1rem;
  color: #e5e7eb;
  padding: 16px 28px;
  letter-spacing: 0.4px;
  transition: color 0.2s ease, background 0.2s ease;
  &:hover {
    color: #c89432;
    background: #1f2937;
  }
`;

const SidebarPhone = styled.a`
  display: flex;
  align-items: center;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 1rem;
  color: #c89432;
  padding: 24px 28px 12px;
`;

const SidebarFooter = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.75rem;
  color: #4b5563;
  text-align: center;
  padding: 12px 24px 24px;
`;

export default Navbar;
